import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { SakerhetsfilmShowcase } from "@/components/sections/SakerhetsfilmShowcase";

import { IMAGES, BBN_PHOTOS } from "@/lib/images";

export const FAQS: FaqItem[] = [
  {
    q: "Vad säger GDPR om insyn i kontorslokaler?",
    a: "GDPR (artikel 32) kräver tekniska och organisatoriska åtgärder som säkerställer en lämplig säkerhetsnivå för personuppgifter. För kontor där känsliga personuppgifter behandlas — HR, ekonomi, sjukvård, juridik — är fysisk avskärmning av skärmar mot insyn en dokumenterad åtgärd. Frostfilm och spegelfilm på glasväggar och fönster i markplan minskar risken för att förbipasserande, besökare eller obehöriga ser personuppgifter på skärmar.",
  },
  {
    q: "Hur skyddar säkerhetsfilm mot inbrott i kontor?",
    a: "Kontor i markplan eller med tillgängliga balkonger är inbrottsutsatta — främst för datastöld (laptops, hårddiskar) och företagsspionage. Säkerhetsfilm enligt EN 12600 klass 2B2 fördröjer ett krossförsök i 30–90 sekunder, vilket räcker för att larmcentralen ska hinna agera och inkräktaren oftast avbryter försöket.",
  },
  {
    q: "Vad gäller för ID06 och säkerhetsfilm vid kontorsrenovering?",
    a: "Vid installation i lokaler där byggnads- och anläggningsarbete pågår behöver montörer ID06-kort enligt branschens överenskommelse. Vi är ID06-anslutna och kan delta i kontorsrenoveringar, ombyggnader och nybyggnader där huvudentreprenören kräver ID06-personal på arbetsplatsen.",
  },
  {
    q: "Försvinner dagsljuset om vi sätter spegelfilm på glasväggar?",
    a: "Nej. Modern spegel- och frostfilm släpper igenom 30–70 % av det synliga ljuset beroende på modell, vilket är tillräckligt för en normal kontorsmiljö. Frostfilm för glasväggar släpper igenom nästan allt ljus men bryter sikten. Det passar mötesrum, enskilda kontorsrum och tysta zoner.",
  },
  {
    q: "Kan ni kombinera insynsskydd med varumärkesprofilering?",
    a: "Ja. Vi installerar dekorfilm med logotyp, skurna mönster eller heltäckande frostat utseende. Det är vanligt i receptioner, mötesrum och på entréglas. Du får både GDPR-säkrat insynsskydd och företagets profil på glaset.",
  },
  {
    q: "Hur ser installation ut för ett aktivt kontor?",
    a: "Vi brukar ta det rum för rum, så att ni kan boka om möten en dag i förväg. Arbetet är dammfritt och rummet kan användas direkt efter installation. För större kontor (1 000+ m² glas) levererar vi rullande schema över 2–3 veckor.",
  },
];

export const Route = createFileRoute("/sakerhetsfilm-kontor")({
  head: () => ({
    meta: [
      { title: "Säkerhetsfilm & insynsskydd för kontor | GDPR & EN 12600 | BBN Nordic" },
      { name: "description", content: "Säkerhetsfilm och insynsskydd för kontor — GDPR-säkrad avskärmning, EN 12600-inbrottsskydd och ID06-ansluten installation. Frostfilm, spegelfilm och splitterskydd för moderna arbetsplatser." },
      { property: "og:title", content: "Säkerhetsfilm för kontor" },
      { property: "og:description", content: "GDPR-säkrad insynsskydd, inbrottsfördröjning enligt EN 12600 och ID06-ansluten installation för kontor i Skåne och Göteborg." },
      { property: "og:url", content: "https://bbnnordic.se/sakerhetsfilm-kontor" },
      { property: "og:image", content: BBN_PHOTOS.sakerhetsfilmKontorHero },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/sakerhetsfilm-kontor" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Säkerhetsfilm och insynsskydd för kontor",
          serviceType: "Säkerhetsfilm, insynsskydd och dekorfilm för kontorslokaler",
          description: "Insynsskydd för GDPR-känsliga arbetsplatser, säkerhetsfilm enligt EN 12600 och ID06-ansluten installation.",
          provider: { "@type": "LocalBusiness", "@id": "/#organization", name: "BBN Nordic" },
          areaServed: "SE",
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQS)) },
    ],
  }),
  component: () => (
    <ContentPage
      disableCta
      eyebrow="BRANSCH — KONTOR"
      title="Säkerhetsfilm för kontor"
      subtitle="GDPR-säkrad insynsskydd, inbrottsfördröjning enligt EN 12600 och ID06-ansluten installation för arbetsplatser där personuppgifter och utrustning ska skyddas."
      heroImage={BBN_PHOTOS.sakerhetsfilmKontorHero}
      breadcrumbs={[{ label: "Säkerhetsfilm kontor" }]}
      sections={[
        {
          eyebrow: "GDPR & ARBETSMILJÖ",
          heading: "Insynsskydd som dokumenterad GDPR-åtgärd.",
          paragraphs: [
            "GDPR artikel 32 kräver lämpliga tekniska och organisatoriska åtgärder för att skydda personuppgifter. På kontor inom HR, ekonomi, juridik, sjukvård och offentlig förvaltning kan fysiskt insynsskydd skydda uppgifter från obehöriga blickar. Åtgärden går att dokumentera i organisationens säkerhetsanalys.",
            "Frostfilm på mötesrum, spegelfilm på fönster i markplan och avskärmande dekorfilm på glasväggar mellan zoner är vanliga lösningar. Vi utgår från er informationsklassning och föreslår film som bevarar ljusinsläppet och kontorets öppna känsla.",
          ],
          bullets: [
            "GDPR art. 32 — krav på tekniska säkerhetsåtgärder",
            "AFS 2020:1 § 30 — krav på termiskt klimat (kombineras ofta med solfilm)",
            "AFS 2020:1 § 35 — krav på dagsljus och åtgärder mot bländning",
            "AFS 1998:5 — krav på skärmarbetsplats (insyn mot skärmar)",
            "ISO 27001 A.11.1 — fysisk säkerhet för informationshantering",
          ],
          image: BBN_PHOTOS.dekorGlass,
        },
        {
          eyebrow: "INBROTT & FÖRETAGSSPIONAGE",
          heading: "Säkerhetsfilm för kontor i markplan.",
          paragraphs: [
            "Vid kontorsinbrott är det sällan kontanter som stjäls. Det är oftare laptops, hårddiskar, kundregister och prototyper. Brå:s statistik visar att inbrott i företagslokaler ökade 12 % under den senaste mätperioden. Fönster i markplan och balkongdörrar på låg höjd är vanliga vägar in.",
            "Säkerhetsfilm enligt EN 12600 klass 2B2 håller ihop glaset 30–90 sekunder vid spontana krossförsök. Det räcker för att larmcentralen ska hinna larma ut väktarbil och inkräktaren avbryter oftast försöket. Vid kombinationsskydd (film + larm + kameror) ger flera försäkringsbolag premiereduktion.",
          ],
          bullets: [
            "EN 12600 klass 2B2 — standardrekommendation för kontor i markplan",
            "EN 12600 klass 1B1 — högsta klass för känsliga rum (serverrum, dataarkiv)",
            "Försäkringsvillkor — refererar till EN 12600-klass",
            "Personskadeskydd — vid explosioner eller incident med flygande glas",
          ],
          image: BBN_PHOTOS.installation5,
        },
        {
          eyebrow: "ID06 & ENTREPRENÖRSKRAV",
          heading: "ID06-anslutna montörer för byggnads- och ombyggnadsprojekt.",
          paragraphs: [
            "ID06 är branschens system för säker identifiering av personal på byggarbetsplats — krav i de flesta entreprenadkontrakt enligt Byggföretagens standardvillkor. Vid kontorsrenovering, om- eller nybyggnad där huvudentreprenören driver projektet behöver underentreprenörer ID06-kort, F-skatt och fullgjorda försäkringar.",
            "Vi är ID06-anslutna, har giltig F-skatt, kollektivavtal och samtliga ansvarsförsäkringar. Vi kan delta i nybyggnationer (BAS-U/BAS-P-projekt), kontorsrenoveringar och hyresgästanpassningar utan att huvudentreprenören behöver göra extra kontroller.",
          ],
          bullets: [
            "ID06 — branschstandard för identifiering på byggarbetsplats",
            "F-skatt och kollektivavtal (Byggnads/Måleri)",
            "Ansvars- och entreprenadförsäkring",
            "BAS-U/BAS-P-erfarenhet — anpassad till huvudentreprenörens AMP",
            "Auktoriserad 3M-installatör — projektgaranti registreras på objektet",
          ],
          image: IMAGES.sakerhetsfilmHero,
        },
      ]}
      related={[
        { to: "/insynsskydd-fonster", label: "Insynsskydd", desc: "Spegelfilm och dagtidsfilm för fönster." },
        { to: "/dekorfilm", label: "Dekor- & frostfilm", desc: "Frostat utseende och varumärkesprofilering." },
        { to: "/sakerhetsfilm", label: "Säkerhetsfilm", desc: "Hela säkerhetssortimentet." },
        { to: "/offert", label: "Begär offert", desc: "Underlag för fastighetschef eller säkerhetsansvarig." },
      ]}
      priorityContent={<SakerhetsfilmShowcase />}
    >
      <FaqSection heading="Vanliga frågor från säkerhetsansvariga och kontorsansvariga." items={FAQS} />
    </ContentPage>
  ),
});
